import { Tooltip, Typography } from '@mui/material'
import { get, isEmpty } from 'lodash'
import { useTranslate } from 'ra-core'
import { Fragment, useState } from 'react'
import { useNotify, useResourceContext } from 'react-admin'
import { Link } from 'react-router-dom'

import SensitiveDataAccessDialog from './SensitiveDataAccessDialog'
import StaticText from './StaticText'
import UserDocumentImageDialog from './UserDocumentImageDialog'
import { useApi } from '../api/apiProvider'
import { getErrorDataFromError } from '../config/errors'
import { SYSTEM_PERMISSION_PRIVATE_DATA } from '../config/permissions'
import { useCommonStyles } from '../config/theme'
import {
  USER_DOCUMENT_STATUS_ACCEPTED,
  USER_DOCUMENT_STATUS_MISSING,
  USER_DOCUMENT_STATUS_PENDING,
  USER_DOCUMENT_STATUSES,
  USER_DOCUMENT_STATUSES_ICONS,
  USER_DOCUMENT_TYPES,
} from '../config/users'
import { useResourcePermissions } from '../domain/permissions'
import usersConfig from '../resources/users/config'

const styles = {
  smallIcon: {
    verticalAlign: 'sub',
    height: 20,
    marginTop: -1,
  },
  largeIcon: {
    verticalAlign: 'bottom',
    height: 24,
    marginTop: -4,
  },
}

const IdentificationDataDialog = ({ isOpen, onClose, userId, documentId, title }) => {
  const [data, setData] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [justification, setJustification] = useState('')

  const resource = useResourceContext()
  const notify = useNotify()
  const translate = useTranslate()

  const translationPrefix = `resources.${resource}.show.identificationData`

  const [fetchIdentificationData] = useApi(`/${resource}/${userId}/private-data`, {
    method: 'POST',
    body: JSON.stringify({ justification, user_document_id: documentId }),
    onSuccess: (response) => {
      if (isEmpty(response.json.user_document_image_ids)) {
        onClose()
        setIsLoading(false)
        setJustification('')
        notify(translate(`${translationPrefix}.noAvailableData`), { type: 'info' })
        return
      }

      setData(response.json)
      setTimeout(() => setIsLoading(false), 200)
    },
    onFailure: (error) => {
      setIsLoading(false)
      const { message } = getErrorDataFromError(error)
      notify(translate('mymove.errors.errorWithMessage', { message }), { type: 'error' })
    },
  })

  const handleConfirm = () => {
    if (!justification || isLoading) return
    setIsLoading(true)
    fetchIdentificationData()
  }

  const handleClose = () => {
    onClose()
    setTimeout(() => {
      setData({})
      setIsLoading(false)
      setJustification('')
    }, 200)
  }

  return (
    <>
      <SensitiveDataAccessDialog
        handleClose={handleClose}
        handleConfirm={handleConfirm}
        isConfirmDisabled={!justification}
        isOpen={isOpen && isEmpty(data)}
        loading={isLoading}
        onJustificationChange={(event) => setJustification(event.target.value)}
        translationPrefix={translationPrefix}
      />
      <UserDocumentImageDialog
        isOpen={isOpen}
        userDocumentImageIdList={data.user_document_image_ids}
        handleClose={handleClose}
        translationPrefix={translationPrefix}
        title={title}
      />
    </>
  )
}

const UserDocumentStatusField = ({ record }) => {
  const translate = useTranslate()
  const [hasAccessToPrivateData] = useResourcePermissions(usersConfig.name, SYSTEM_PERMISSION_PRIVATE_DATA)
  const commonClasses = useCommonStyles()

  const [selectedDocument, setSelectedDocument] = useState(null)
  const closeDialog = () => setSelectedDocument(null)

  const documents = get(record, 'documents', [])

  if (record.is_trusted) {
    return <StaticText>resources.users.show.trustedDocumentNotRequired</StaticText>
  }

  return (
    <>
      {Object.keys(USER_DOCUMENT_TYPES).map((documentType) => {
        const document = documents.find((doc) => doc.type === documentType)
        const { status, type } = document
        const Icon = USER_DOCUMENT_STATUSES_ICONS[status]
        const shouldEnableDocumentLink =
          hasAccessToPrivateData &&
          (status === USER_DOCUMENT_STATUS_ACCEPTED || status === USER_DOCUMENT_STATUS_PENDING)

        const documentTypeLabel = translate(USER_DOCUMENT_TYPES[type])
        return (
          <Fragment key={type}>
            <Tooltip title={translate(USER_DOCUMENT_STATUSES[status])}>
              <Icon
                sx={
                  status === USER_DOCUMENT_STATUS_MISSING || status === USER_DOCUMENT_STATUS_PENDING
                    ? styles.smallIcon
                    : styles.largeIcon
                }
              />
            </Tooltip>
            {'\xa0'}
            {shouldEnableDocumentLink ? (
              <Typography component="span" variant="body2" color="primary">
                <Link
                  onClick={() => setSelectedDocument(document)}
                  sx={commonClasses.link}
                  data-testid={`identityDocument-${type}`}
                >
                  {documentTypeLabel}
                </Link>
              </Typography>
            ) : (
              <StaticText>{documentTypeLabel}</StaticText>
            )}
            {'\xa0\xa0\xa0\xa0'}
          </Fragment>
        )
      })}
      <IdentificationDataDialog
        isOpen={Boolean(selectedDocument)}
        onClose={closeDialog}
        userId={record.id}
        documentId={selectedDocument?.id}
        title={USER_DOCUMENT_TYPES[selectedDocument?.type]}
      />
    </>
  )
}

UserDocumentStatusField.defaultProps = {
  label: 'resources.users.enums.documentStatus.name',
}

export default UserDocumentStatusField
